import {
  Box,
  Tooltip,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { appThemes, useMeetingAppContext } from "../MeetingAppContextDef";
import { getAnimationDuration } from "../utils/animationUtils";
import IconButtonBase from "./IconButtonBase";

const OutlineIconButton = ({
  badge,
  onClick,
  Icon,
  isFocused,
  bgColor,
  focusBGColor,
  focusIconColor,
  disabledOpacity,
  disabled,
  large,
  renderRightComponent,
  tooltipTitle,
  btnID,
  buttonText,
  lottieOption,
  isRequestProcessing,
  focusBorderColor,
  color,
}) => {
  const theme = useTheme();
  const [mouseOver, setMouseOver] = useState(false);
  const [mouseDown, setMouseDown] = useState(false);
  const [blinkingState, setBlinkingState] = useState(1);

  const intervalRef = useRef();

  const { animationsEnabled, appTheme } = useMeetingAppContext();

  const startBlinking = () => {
    intervalRef.current = setInterval(() => {
      setBlinkingState((s) => (s === 1 ? 0.4 : 1));
    }, 600);
  };

  const stopBlinking = () => {
    clearInterval(intervalRef.current);
    setBlinkingState(1);
  };

  useEffect(() => {
    if (isRequestProcessing) {
      startBlinking();
    } else {
      stopBlinking();
    }
  }, [isRequestProcessing]);

  useEffect(() => {
    return () => {
      stopBlinking();
    };
  }, []);

  const getBorderColor = () => {
    if (mouseOver || mouseDown) return "transparent";
    if (focusBorderColor) return focusBorderColor;
    if (bgColor) return bgColor;
    if (focusBGColor) {
      return appTheme === appThemes.LIGHT
        ? theme.palette.lightTheme.outlineColor
        : focusBGColor;
    }
    return appTheme === appThemes.LIGHT
      ? theme.palette.lightTheme.outlineColor
      : "#ffffff33";
  };

  const getBackgroundColor = () => {
    if (bgColor) return bgColor;
    if (isFocused) return focusBGColor || "#fff";
    if (appTheme === appThemes.DARK) return theme.palette.darkTheme.main;
    if (appTheme === appThemes.LIGHT) return theme.palette.lightTheme.main;
    return theme.palette.background.default;
  };

  const tooltipStyles = {
    fontSize: '1.4vh',
    padding: '0.8vh 1.2vh',
    borderRadius: '0.5vh',
  };

  const containerStyles = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: '0.8vh',
    backgroundColor: getBackgroundColor(),
    border: `0.2vh solid ${getBorderColor()}`,
    transition: `all ${getAnimationDuration(animationsEnabled, 200)}ms`,
    transitionTimingFunction: "linear",
    opacity: blinkingState,
  };

  return (
    <Tooltip 
      placement="bottom" 
      title={tooltipTitle || ""}
      open={mouseOver || mouseDown}
      slotProps={{
        tooltip: {
          sx: tooltipStyles
        }
      }}
    >
      <Box style={containerStyles}>
        <IconButtonBase
          badge={badge}
          onClick={onClick}
          Icon={Icon}
          isFocused={isFocused}
          bgColor={bgColor}
          focusIconColor={focusIconColor}
          disabledOpacity={disabledOpacity}
          disabled={disabled}
          btnID={btnID}
          buttonText={buttonText}
          lottieOption={lottieOption}
          mouseOver={mouseOver}
          mouseDown={mouseDown}
          onMouseEnter={() => setMouseOver(true)}
          onMouseLeave={() => setMouseOver(false)}
          onMouseDown={() => setMouseDown(true)}
          onMouseUp={() => setMouseDown(false)}
          color={color}
        />
        {typeof renderRightComponent === "function" && renderRightComponent()}
      </Box>
    </Tooltip>
  );
};

export default OutlineIconButton;
